import { Gauge } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  INTERVIEW_MODE_LABELS,
  INTERVIEW_STATUS_LABELS,
  OFFER_STATUS_LABELS,
  WORKSPACE_DECISION_LABELS,
  WORKSPACE_SECTION_LABELS,
  WORKSPACE_SECTIONS,
  workspaceCounts,
  workspaceFigure,
  type RecruiterWorkspace,
  type WorkspaceDecisionKind,
  type WorkspaceSection,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card, MetricStrip } from '../../../components/Card';
import { INTERVIEW_STATUS_TONES, OFFER_STATUS_TONES } from '../../../components/status-tones';
import { ToneChip, ToneIcon, slaTone, toneCardClass, type Tone } from '../../../components/Tone';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { formatDate, formatDateTimeLong, formatNumber, formatRelative, formatTime } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { useCanView, useRequiredSession } from '../../../stores/session';
import { router } from '../../../router';
import { DeepLink } from '../../notifications/components/DeepLink';
import { FunnelBar } from '../../requisitions/components/HmBadges';
import { PriorityDot, RequisitionStatusChip, SlaFigure } from '../../requisitions/components/RequisitionBadges';
import { useWorkspace } from '../api/workspace-api';
import styles from '../Workspace.module.css';

/** What an empty section says — each names what will fill it (06-qol §5). */
const EMPTY_COPY: Readonly<Record<WorkspaceSection, string>> = {
  requisitions: 'No requisitions name you as recruiter yet.',
  sorting: 'No CVs are waiting. New applications land here.',
  interviews: 'Nothing on your calendar today.',
  decisions: 'No rounds, holds or tests are waiting on you.',
  tasks: 'No overdue tasks. Follow-ups you log land in My tasks.',
  offers: 'No offers are waiting on a candidate.',
  sla: 'Everything you answer for is inside its time limit.',
};

const sectionId = (section: WorkspaceSection): string => `workspace-${section}`;

/** A round waits on interviewers, a hold on a place, marking on a test. */
const DECISION_TONES: Readonly<Record<WorkspaceDecisionKind, Tone>> = {
  round: 'interview',
  hold: 'hold',
  marking: 'assessment',
};

/**
 * RPT-02 — the recruiter's daily home (master plan S27). Seven sections of their own records,
 * each row opening the screen where the work is done. Not analytics: no figure here is a rate.
 */
export function WorkspacePage(): ReactNode {
  const canView = useCanView('reports_own');
  const canSeeCommandCenter = useCanView('reports_all_staff');
  const session = useRequiredSession();
  const workspace = useWorkspace(canView);
  // Rule 13 (bug B9): the time-limit section, its tile and every clock's colour belong to Time limits.
  const isSlaOn = useModule('sla');
  const sections = isSlaOn ? WORKSPACE_SECTIONS : WORKSPACE_SECTIONS.filter((section) => section !== 'sla');

  if (!canView) return <ForbiddenState resource="the workspace" contact="Your admin" />;
  if (workspace.error && !workspace.data) return <ServerErrorState onRetry={() => void workspace.refetch()} />;

  const data = workspace.data;
  const counts = data ? workspaceCounts(data) : null;
  const firstName = session.fullName.split(' ')[0] ?? session.fullName;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{greeting(session.timezone)}, {firstName}</h1>
          <p className={styles.lead}>
            {formatDate(new Date())} · Everything waiting on you, in one place.
            {data && (
              <span className={styles.meta} title={formatDateTimeLong(data.generatedAt)}>
                {' '}
                Updated {formatRelative(data.generatedAt)}.
              </span>
            )}
          </p>
        </div>
        {canSeeCommandCenter && (
          <Button variant="outline" icon={Gauge} onClick={() => router.history.push('/reports/command-center')}>
            Command center
          </Button>
        )}
      </header>

      <Card isFlush>
        <MetricStrip
          metrics={sections.map((section) => ({
            label: WORKSPACE_SECTION_LABELS[section],
            value: counts ? formatNumber(counts[section]) : '…',
            onClick: () => document.getElementById(sectionId(section))?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
          }))}
        />
      </Card>

      <div className={styles.grid}>
        <Section section="interviews" data={data} counts={counts}>
          {data?.interviews.items.map((row) => (
            <Row key={row.interviewId} tone="interview" href={row.href} title={`${formatTime(row.startsAt)} · ${row.candidateName}`} meta={`${row.roundName} · ${row.jobTitle} · ${INTERVIEW_MODE_LABELS[row.mode]}${row.location ? ` · ${row.location}` : ''}`}>
              {row.isPanelist && <ToneChip tone="interview">On the panel</ToneChip>}
              <ToneChip tone={INTERVIEW_STATUS_TONES[row.status]}>{INTERVIEW_STATUS_LABELS[row.status]}</ToneChip>
            </Row>
          ))}
        </Section>

        <Section section="sorting" data={data} counts={counts}>
          {data?.sorting.items.map((row) => (
            <Row key={row.jobId} tone="screening" href={row.href} title={row.title} meta={`${row.reference}${row.oldestAppliedAt ? ` · oldest ${formatRelative(row.oldestAppliedAt)}` : ''}`}>
              <span className={styles.figure}>{workspaceFigure('sorting', row.waiting)}</span>
            </Row>
          ))}
        </Section>

        <Section section="decisions" data={data} counts={counts}>
          {data?.decisions.items.map((row) => (
            <Row
              key={row.key}
              href={row.href}
              tone={row.isOverdue ? 'urgent' : DECISION_TONES[row.kind]}
              title={row.candidateName}
              meta={`${row.detail} · ${row.dueAt ? `${row.isOverdue ? 'ended' : 'ends'} ${formatRelative(row.dueAt)}` : `waiting ${formatRelative(row.since)}`}`}
            >
              <ToneChip tone={row.isOverdue ? 'urgent' : DECISION_TONES[row.kind]}>{row.kind === 'hold' && row.dueAt && row.isOverdue ? 'Hold ended' : WORKSPACE_DECISION_LABELS[row.kind]}</ToneChip>
            </Row>
          ))}
        </Section>

        <Section section="tasks" data={data} counts={counts}>
          {data?.tasks.items.map((row) => (
            <Row key={row.id} href={row.href} tone="urgent" title={row.title} meta={[row.candidateName, row.jobTitle, `due ${formatRelative(row.dueAt)}`].filter(Boolean).join(' · ')} />
          ))}
        </Section>

        <Section section="offers" data={data} counts={counts}>
          {data?.offers.items.map((row) => (
            <Row
              key={row.offerId}
              href={row.href}
              tone={row.isExpiringSoon ? 'attention' : 'offer'}
              title={row.candidateName}
              meta={`${row.jobTitle}${row.sentAt ? ` · sent ${formatRelative(row.sentAt)}` : ''}${row.validUntil ? ` · valid until ${formatDate(row.validUntil)}` : ''}`}
            >
              <ToneChip tone={row.isExpiringSoon ? 'attention' : OFFER_STATUS_TONES[row.status]}>{OFFER_STATUS_LABELS[row.status]}</ToneChip>
            </Row>
          ))}
        </Section>

        {isSlaOn && (
        <Section section="sla" data={data} counts={counts}>
          {data?.sla.items.map((row) => (
            <Row key={row.key} href={row.href} tone={slaTone(row.sla.state, row.entityType === 'requisition' ? 'requisition' : 'job')} title={row.label} meta={row.detail}>
              <SlaFigure sla={row.sla} showLimit />
            </Row>
          ))}
        </Section>
        )}

        <Section section="requisitions" data={data} counts={counts} isWide>
          {data?.requisitions.items.map((row) => {
            const tone = isSlaOn && row.sla ? slaTone(row.sla.state, 'requisition') : 'requisition';
            return (
              <li key={row.id} className={`${styles.requisition} ${toneCardClass(true)}`} data-tone={tone}>
                <ToneIcon tone={tone} />
                <DeepLink to={row.href} className={styles.rowLink}>
                  <span className={styles.rowTitle}>
                    {row.reference} · {row.title}
                  </span>
                  <span className={styles.meta}>{[row.departmentName, row.ventureName, `${row.slotsRequested} to hire`].filter(Boolean).join(' · ')}</span>
                </DeepLink>
                <span className={styles.badges}>
                  <RequisitionStatusChip status={row.status} />
                  <PriorityDot priority={row.priority} />
                  {row.sla && <SlaFigure sla={row.sla} />}
                  {row.applicationsAtRisk > 0 && <ToneChip tone="attention">{row.applicationsAtRisk} at risk</ToneChip>}
                </span>
                <FunnelBar funnel={row.funnel} />
              </li>
            );
          })}
        </Section>
      </div>
    </div>
  );
}

/** Each list wears the colour of the part of hiring it is about (DECISIONS-LOG "Colour per section"). */
const SECTION_CARD_TONES: Readonly<Record<WorkspaceSection, Tone>> = {
  requisitions: 'requisition',
  sorting: 'screening',
  interviews: 'interview',
  decisions: 'interview',
  tasks: 'tasks',
  offers: 'offer',
  sla: 'requisition',
};

function Section({
  section,
  data,
  counts,
  isWide = false,
  children,
}: {
  readonly section: WorkspaceSection;
  readonly data: RecruiterWorkspace | undefined;
  readonly counts: Readonly<Record<WorkspaceSection, number>> | null;
  readonly isWide?: boolean;
  readonly children: ReactNode;
}): ReactNode {
  const count = counts?.[section] ?? 0;
  const shown = data ? data[section].items.length : 0;
  return (
    <div id={sectionId(section)} className={styles.section} data-wide={isWide}>
      <Card title={WORKSPACE_SECTION_LABELS[section]} meta={counts ? workspaceFigure(section, count) : undefined} tone={SECTION_CARD_TONES[section]}>
        {!data ? (
          <SkeletonLines count={3} />
        ) : shown === 0 ? (
          <p className={styles.empty}>{EMPTY_COPY[section]}</p>
        ) : (
          <>
            <ul className={styles.rows}>{children}</ul>
            {section !== 'sorting' && count > shown && <p className={styles.meta}>Showing the first {shown} of {formatNumber(count)}.</p>}
          </>
        )}
      </Card>
    </div>
  );
}

function Row({
  href,
  title,
  meta,
  tone,
  children,
}: {
  readonly href: string;
  readonly title: string;
  readonly meta: string;
  /** Overdue / due soon / done when time is in play, else the part of hiring the row is (ADR 0035). */
  readonly tone: Tone;
  readonly children?: ReactNode;
}): ReactNode {
  return (
    <li className={`${styles.row} ${toneCardClass(true)}`} data-tone={tone}>
      <ToneIcon tone={tone} />
      <DeepLink to={href} className={styles.rowLink}>
        <span className={styles.rowTitle}>{title}</span>
        <span className={styles.meta}>{meta}</span>
      </DeepLink>
      {children && <span className={styles.badges}>{children}</span>}
    </li>
  );
}

/** By the viewer's own clock (D13). */
function greeting(timeZone: string): string {
  const hour = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hourCycle: 'h23', timeZone }).format(new Date()));
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
